/**
 * EVENTSFLO-ADMIN-BACKUP-OFFSITE-COPIES-HISTORY-V1
 *
 * Read-only Offsite Copies history table. Calls
 * `GET /api/admin/backup/offsite-copies` (allow-list projection;
 * sha256 truncated to first 16 chars; no absolute path). NO
 * mutating buttons — only Refresh + Prev/Next.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { CloudCog, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { listOffsiteCopies } from "./backupApi";
import { formatBytes, formatDate, PAGE_SIZE } from "./backupFormatters";

const _STATUS_COLOR = {
  ok: "bg-green-500/20 text-green-400 border-green-500/30",
  failed: "bg-red-500/20 text-red-400 border-red-500/30",
};

function StatusBadge({ status }) {
  const cls = _STATUS_COLOR[status] || "bg-zinc-500/20 text-zinc-300 border-zinc-500/30";
  return <Badge className={cls}>{status || "—"}</Badge>;
}

function shortId(id) {
  if (!id) return "—";
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

export default function BackupOffsiteCopiesHistory() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async (newOffset) => {
    setLoading(true);
    try {
      const { data } = await listOffsiteCopies({
        limit: PAGE_SIZE,
        offset: newOffset,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load offsite copies history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows(0);
  }, [fetchRows]);

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card data-testid="offsite-copies-history">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <CloudCog className="w-4 h-4" /> Offsite copies history ({total})
        </CardTitle>
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span>
            Page {currentPage} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const o = Math.max(0, offset - PAGE_SIZE);
              setOffset(o);
              fetchRows(o);
            }}
            disabled={offset === 0}
            data-testid="offsite-copies-prev-btn"
          >
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const o = offset + PAGE_SIZE;
              setOffset(o);
              fetchRows(o);
            }}
            disabled={offset + PAGE_SIZE >= total}
            data-testid="offsite-copies-next-btn"
          >
            Next
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchRows(offset)}
            data-testid="refresh-offsite-copies-btn"
          >
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-zinc-400">Loading…</div>
        ) : items.length === 0 ? (
          <div
            className="text-sm text-zinc-400 py-8 text-center"
            data-testid="offsite-copies-empty-state"
          >
            No offsite copies yet.
          </div>
        ) : (
          <Table data-testid="offsite-copies-table">
            <TableHeader>
              <TableRow>
                <TableHead>Backup</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Bytes</TableHead>
                <TableHead>SHA256 (16)</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Started → Completed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow
                  key={r.copy_id}
                  data-testid={`offsite-copy-row-${r.copy_id}`}
                >
                  <TableCell
                    className="font-mono text-xs"
                    title={r.backup_id}
                  >
                    {shortId(r.backup_id)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-xs">{r.target_label}</TableCell>
                  <TableCell className="text-xs">{formatBytes(r.bytes_copied)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.sha256_prefix ? `${r.sha256_prefix}…` : "—"}
                  </TableCell>
                  <TableCell className="text-xs">{r.actor_email || "—"}</TableCell>
                  <TableCell className="text-xs">
                    <div>{formatDate(r.started_at)}</div>
                    <div className="text-zinc-500">→ {formatDate(r.completed_at)}</div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
